import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '../ui/Button';
import { ShieldCheck } from 'lucide-react';

interface MfaChallengeProps {
  /** The short-lived challenge token returned by /auth/login. */
  mfaToken: string;
  /** Called with the entered code; should resolve when login completes. */
  onSubmit: (code: string) => Promise<void>;
  /** Return to the email/password screen. */
  onCancel: () => void;
  /** When true, the account is required to enroll but has not yet. */
  enrollmentRequired?: boolean;
}

const CODE_LENGTH = 6;

/**
 * Second-factor entry screen. Shows six auto-advancing digit boxes for a TOTP
 * code, with a fallback to enter a recovery code. Matches the login page's
 * visual style (emerald/slate, centered card).
 */
const MfaChallenge: React.FC<MfaChallengeProps> = ({
  mfaToken,
  onSubmit,
  onCancel,
  enrollmentRequired = false,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (!recoveryMode) inputsRef.current[0]?.focus();
  }, [recoveryMode]);

  const submitCode = useCallback(
    async (code: string) => {
      setError(null);
      setIsSubmitting(true);
      try {
        await onSubmit(code);
        // On success the parent navigates away; nothing else to do here.
      } catch (err) {
        const message =
          (err as { message?: string })?.message || 'Verification failed';
        setError(
          message.includes('Invalid') || message.includes('code')
            ? 'That code was not valid. Please try again.'
            : 'Verification failed. Please try again.'
        );
        setDigits(Array(CODE_LENGTH).fill(''));
        inputsRef.current[0]?.focus();
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit]
  );

  const handleDigitChange = (index: number, value: string) => {
    const clean = value.replace(/\D/g, '');
    if (!clean) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }
    // Support pasting the full code into any box.
    if (clean.length > 1) {
      const chars = clean.slice(0, CODE_LENGTH).split('');
      const next = Array(CODE_LENGTH).fill('');
      chars.forEach((c, i) => (next[i] = c));
      setDigits(next);
      const lastFilled = Math.min(chars.length, CODE_LENGTH) - 1;
      inputsRef.current[lastFilled]?.focus();
      if (chars.length >= CODE_LENGTH) void submitCode(next.join(''));
      return;
    }
    const next = [...digits];
    next[index] = clean;
    setDigits(next);
    if (index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
    if (index === CODE_LENGTH - 1 && next.every((d) => d)) {
      void submitCode(next.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleRecoverySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (recoveryCode.trim()) void submitCode(recoveryCode.trim());
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4 border border-slate-200">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">
            Two-factor authentication
          </h2>
          <p className="text-slate-600">
            {enrollmentRequired
              ? 'Your organization requires two-factor authentication. Please finish setting it up.'
              : recoveryMode
              ? 'Enter one of your saved recovery codes.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!recoveryMode ? (
          <>
            <div className="flex justify-center gap-2 mb-6" role="group" aria-label="Authentication code">
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={CODE_LENGTH}
                  value={digit}
                  disabled={isSubmitting}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  aria-label={`Digit ${i + 1}`}
                  className="w-12 h-14 text-center text-xl font-semibold rounded-lg border border-slate-300 bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition disabled:opacity-50"
                />
              ))}
            </div>
            <Button
              type="button"
              className="w-full"
              disabled={isSubmitting || digits.some((d) => !d)}
              onClick={() => submitCode(digits.join(''))}
            >
              {isSubmitting ? 'Verifying...' : 'Verify'}
            </Button>
            <div className="mt-4 text-center">
              <button
                type="button"
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                onClick={() => {
                  setRecoveryMode(true);
                  setError(null);
                }}
              >
                Use a recovery code instead
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleRecoverySubmit}>
            <input
              type="text"
              value={recoveryCode}
              disabled={isSubmitting}
              autoFocus
              placeholder="xxxxx-xxxxx"
              onChange={(e) => setRecoveryCode(e.target.value)}
              aria-label="Recovery code"
              className="w-full h-12 px-4 mb-6 rounded-lg border border-slate-300 bg-white text-slate-900 text-center tracking-wider focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition disabled:opacity-50"
            />
            <Button type="submit" className="w-full" disabled={isSubmitting || !recoveryCode.trim()}>
              {isSubmitting ? 'Verifying...' : 'Verify recovery code'}
            </Button>
            <div className="mt-4 text-center">
              <button
                type="button"
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                onClick={() => {
                  setRecoveryMode(false);
                  setError(null);
                }}
              >
                Back to authenticator code
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            type="button"
            className="text-sm text-slate-500 hover:text-slate-700"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel and sign in again
          </button>
        </div>
      </div>
    </div>
  );
};

export default MfaChallenge;
